import React from 'react';
import { Box, Typography, Link } from '@mui/material';

/**
 * Terms of Service for the "Generate Agreement (Platform)" service.
 * Covers: free credits (1 per user), paid credits at platform fee (default $8.98, configurable in admin),
 * and agreement to these terms before using the generated agency agreement.
 * Used in AgencyAgreementTermsDialog and optionally linked from the main Terms page.
 */
export const AgencyAgreementGenerationTermsContent = () => (
  <Box>
    <Typography variant="body1" paragraph>
      These Terms of Service (&quot;Agreement Generation Terms&quot;) apply specifically to your use of the
      <strong> Generate Agreement (Platform) </strong>
      service on Workflow.Estate (&quot;Platform&quot;), operated by CodePulse Pty Ltd (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;).
      By using this service to generate an agency agreement document, you agree to these terms in addition to the
      general Platform Terms of Service.
    </Typography>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        1. Description of the Service
      </Typography>
      <Typography variant="body1" paragraph>
        The Generate Agreement (Platform) service allows authorised users to generate agency agreement documents
        (e.g. exclusive agency agreements, listing agreements) for use in connection with properties managed on the
        Platform. The generated document is produced from the data you provide (property, workflow, commission and
        marketing terms, etc.) and is intended to support your engagement with vendors. The document is for
        professional use in accordance with applicable real estate and consumer laws.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        2. Free and Paid Credits
      </Typography>
      <Typography variant="body1" paragraph>
      Each user is allocated <strong>one (1) free credit</strong> per calendar month for generating agency
      agreements via this service. Free credits are applied per user, per month; they do not roll over. Once you
      have used your free credit for the month, additional generations are charged to your agency&apos;s credit
        balance at the <strong>per-generation fee set by the platform</strong> (the default fee is $8.98 AUD per
        generation, unless otherwise displayed in the product or in the system admin portal).
      </Typography>
      <Typography variant="body1" paragraph>
        The per-generation fee may be configured by the platform administrator and may change from time to time.
        The current fee and your agency&apos;s credit balance are shown in the product before you confirm generation.
        You are responsible for ensuring your agency has sufficient credits (or free generations available) before
        requesting a generation. Charges are deducted from your agency&apos;s campaign/agreement credit balance at the
        time of generation. One charge applies per property for the first generation for that property; regenerations
        for the same property (e.g. after vendor signatures) may not incur an additional charge as described in the
        product.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        3. Use of the Generated Agency Agreement
      </Typography>
      <Typography variant="body1" paragraph>
        The agency agreement document generated by the service is provided for your use in connection with the
        relevant property and vendor. You agree to use it only for lawful purposes and in compliance with all
        applicable laws, including real estate and consumer protection legislation. You are responsible for ensuring
        that the terms you enter (commission, periods, marketing costs, etc.) are accurate and appropriate for your
        jurisdiction and for obtaining any legal or professional advice you need before using the document with
        vendors.
      </Typography>
      <Typography variant="body1" paragraph>
        The generated document is not legal advice. We do not guarantee that it will be suitable for every
        situation or jurisdiction. You assume full responsibility for the content and use of the agreement in your
        dealings with vendors and for obtaining any required signatures and consents.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        4. Acceptance Required
      </Typography>
      <Typography variant="body1" paragraph>
        By proceeding to generate an agency agreement, you confirm that you have read, understood, and agree to
        these Agreement Generation Terms. You also confirm that you are authorised to use this service on behalf of
        your agency and to bind your agency to these terms. If you do not agree, you must not use the Generate
        Agreement (Platform) service.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        5. No Warranty; Limitation of Liability
      </Typography>
      <Typography variant="body1" paragraph>
        The service and generated documents are provided &quot;as is&quot;. To the maximum extent permitted by law, we
        disclaim all warranties (express or implied) regarding the service and the generated agreement. We shall
        not be liable for any loss or damage arising from your use of the generated document or your reliance on it,
        including but not limited to claims by vendors or third parties. Our total liability in connection with this
        service is limited to the amount you paid for the specific generation in question (if any).
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        6. Changes to These Terms and to Fees
      </Typography>
      <Typography variant="body1" paragraph>
        We may update these Agreement Generation Terms and the per-generation fee from time to time. Fee changes
        may be reflected in the product and/or in the system admin portal. Your continued use of the service after
        such changes constitutes acceptance of the updated terms and fees. We encourage you to review these terms
        periodically.
      </Typography>
    </Box>

    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
        7. Contact
      </Typography>
      <Typography variant="body1" paragraph>
        For questions about the Generate Agreement (Platform) service or these terms, contact us at{' '}
        <Link href="mailto:support@workflow.estate" underline="hover">
          support@workflow.estate
        </Link>
        .
      </Typography>
    </Box>
  </Box>
);
